import { supabase } from "@/integrations/supabase/client";
import { urlBase64ToUint8Array } from "@/lib/pwa";

type PushPayload = {
  userId: string;
  notificationId: string;
  title: string;
  message: string;
  link?: string | null;
  type: string;
};

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

export function supportsPushNotifications() {
  return typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    Boolean(VAPID_PUBLIC_KEY);
}

export async function requestPushPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "denied" as NotificationPermission;
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  if (Notification.permission === "denied") {
    return "denied";
  }

  return Notification.requestPermission();
}

export async function ensurePushSubscription() {
  if (!supportsPushNotifications() || Notification.permission !== "granted") {
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const subscriptionJson = subscription.toJSON();

  await supabase.functions.invoke("save-push-subscription", {
    body: {
      subscription: subscriptionJson,
      userAgent: navigator.userAgent,
    },
  });

  return subscription;
}

export async function dispatchNativePush(payload: PushPayload) {
  if (!supportsPushNotifications() || Notification.permission !== "granted") {
    return;
  }

  await supabase.functions.invoke("send-web-push", {
    body: payload,
  });
}
